import React, { useCallback, useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import "./ImagePreview.css";

interface ImagePreviewProps {
  value?: File | null;
  onChange: (file: File | null) => void;
  isValid?: (isValid: boolean) => void;
}

const ImagePreview: React.FC<ImagePreviewProps> = ({
  value,
  onChange,
  isValid = () => {},
}) => {
  const isValidRef = useRef(isValid);
  useEffect(() => {
    isValidRef.current = isValid;
  }, [isValid]);

  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const zoomRef = useRef(1);

  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [originalPhoto, setOriginalPhoto] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [isCropped, setIsCropped] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);

  const [zoom, setZoom] = useState(1);
  const [zoomMin, setZoomMin] = useState(1);

  const handleImageLoaded = useCallback(
    (mediaSize: { width: number; height: number }) => {
      const cropSize = 250;
      const minZoomCalculated = Math.max(
        cropSize / mediaSize.width,
        cropSize / mediaSize.height,
      );

      setZoomMin(minZoomCalculated);
      const safeZoom = Math.max(zoomRef.current || 1, minZoomCalculated);
      setZoom(safeZoom);
      zoomRef.current = safeZoom;
      setCrop({ x: 0, y: 0 });
    },
    [],
  );

  const onCropComplete = useCallback((_: any, croppedPixels: any) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhotoError(null);

    if (!e.target.files?.[0]) {
      setPhotoError("Falha ao selecionar a foto.");
      return;
    }

    const file = e.target.files[0];
    const previewUrl = URL.createObjectURL(file);

    setPhotoPreview(previewUrl);
    setOriginalPhoto(previewUrl);
    setIsCropped(false);
    isValidRef.current(false);

    setCrop({ x: 0, y: 0 });
    setZoom(1);
    zoomRef.current = 1;
    setCroppedAreaPixels(null);
  };

  const getCroppedImg = async (imageSrc: string, crop: any) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.src = imageSrc;

    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = reject;
    });

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    const size = 512;
    canvas.width = size;
    canvas.height = size;

    ctx.drawImage(
      image,
      crop.x,
      crop.y,
      crop.width,
      crop.height,
      0,
      0,
      size,
      size,
    );

    return new Promise<File | null>((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) return resolve(null);
          resolve(
            new File([blob], "profile.jpg", {
              type: "image/jpeg",
              lastModified: Date.now(),
            }),
          );
        },
        "image/jpeg",
        0.92,
      );
    });
  };

  const applyCrop = async () => {
    if (!originalPhoto || !croppedAreaPixels) return;
    setIsCropping(true);
    try {
      const croppedFile = await getCroppedImg(originalPhoto, croppedAreaPixels);
      if (!croppedFile) {
        setPhotoError("Erro ao aplicar corte.");
        isValidRef.current(false);
        return;
      }
      onChange(croppedFile);
      isValidRef.current(true);
      setIsCropped(true);
    } catch (e) {
      setPhotoError("Erro ao aplicar corte.");
      isValidRef.current(false);
    } finally {
      setIsCropping(false);
    }
  };

  const handleRemovePhoto = () => {
    onChange(null);
    setPhotoPreview(null);
    setOriginalPhoto(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    zoomRef.current = 1;
    setCroppedAreaPixels(null);
    setPhotoError(null);
    setIsCropped(false);
    isValidRef.current(false);

    if (photoInputRef.current) {
      photoInputRef.current.value = "";
    }
  };

  // Sincroniza o estado interno apenas se o value mudar de fora e for diferente do atual
  useEffect(() => {
    if (!value) {
      // Se a prop virar null de fora, reseta o preview interno
      setPhotoPreview(null);
      setOriginalPhoto(null);
      setIsCropped(false);
      return;
    }

    const previewUrl = URL.createObjectURL(value);
    setPhotoPreview(previewUrl);
    setOriginalPhoto(previewUrl);
    setIsCropped(true);

    return () => {
      URL.revokeObjectURL(previewUrl);
    };
  }, [value]);

  return (
    <div>
      <label htmlFor="photo-upload" className="photo-upload-container">
        {!photoPreview && (
          <a
            className="photo-upload-placeholder"
            onClick={() => {
              const input = document.createElement("input");
              input.type = "file";
              input.accept = "image/*";
              input.onchange = (e) => handlePhotoChange(e as any);
              input.click();
            }}
          >
            <i className="fas fa-camera photo-icon">📷</i>
            <span>Escolher foto</span>
          </a>
        )}

        {photoPreview ? (
          <>
            <div
              style={{
                position: "relative",
                width: 250,
                height: 250,
                margin: "20px auto",
                background: "#111",
                borderRadius: "50%",
                overflow: "hidden",
              }}
            >
              <Cropper
                image={photoPreview}
                crop={crop}
                zoom={Math.max(zoom, zoomMin)}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={isCropped ? () => {} : setCrop}
                onZoomChange={
                  isCropped ? () => {} : (val) => setZoom(Number(val))
                }
                onCropComplete={isCropped ? () => {} : onCropComplete}
                onMediaLoaded={handleImageLoaded}
                minZoom={zoomMin}
                maxZoom={3}
                objectFit="cover"
                restrictPosition
                style={
                  isCropped
                    ? { containerStyle: { cursor: "default" } }
                    : undefined
                }
              />
              {isCropping && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    background: "rgba(0,0,0,0.5)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 10,
                  }}
                >
                  <div
                    className="photo-crop-loading"
                    style={{ color: "#fff", fontSize: 28 }}
                  >
                    <span
                      className="loader"
                      style={{
                        display: "inline-block",
                        width: 40,
                        height: 40,
                        border: "4px solid #fff",
                        borderTop: "4px solid #888",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                        marginRight: 12,
                      }}
                    />
                  </div>
                  <style>{`@keyframes spin { 0% { transform: rotate(0deg);} 100% { transform: rotate(360deg);} }`}</style>
                </div>
              )}
            </div>

            {!isCropping && photoPreview && !isCropped && (
              <>
                <input
                  className="photo-zoom-slider"
                  type="range"
                  min={zoomMin}
                  max={3}
                  step={0.01}
                  value={Math.max(zoom, zoomMin)}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  style={{ width: 200 }}
                />

                <div
                  style={{
                    fontSize: 12,
                    color: "#888",
                    marginTop: 14,
                  }}
                >
                  Ajuste o zoom e arraste a imagem para enquadrar
                </div>
              </>
            )}

            <div className="photo-actions">
              {!isCropping && photoPreview && !isCropped && (
                <button
                  type="button"
                  onClick={applyCrop}
                  className="confirm-photo-btn"
                >
                  Confirmar
                </button>
              )}

              <button
                type="button"
                onClick={handleRemovePhoto}
                className="remove-photo-btn"
              >
                Remover
              </button>
            </div>
          </>
        ) : null}
      </label>

      {photoError && (
        <div
          className="error-message"
          style={{
            color: "#c00",
            marginTop: 8,
          }}
        >
          {photoError}
        </div>
      )}

      <p className="photo-disclaimer">
        Não permitimos fotos fakes; geradas por IA; que mostrem menores de
        idade; ou com conteúdo sexualmente explícito.
      </p>
    </div>
  );
};

export default ImagePreview;
